"use client";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { readPartnerBookingOrigin } from "@/utils/partner/PartnerContext";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.sukuto.UserApp&pcampaignid=web_share";
const APP_STORE_URL =
  "https://apps.apple.com/in/app/sukuto/id6757588572";
const STORAGE_KEY = "smartAppBannerDismissed";

const SmartAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const [storeUrl, setStoreUrl] = useState(PLAY_STORE_URL);
  const pathname = usePathname();
  // Read the partner-origin cookie during render. The read is SSR-safe (returns
  // null on the server) and the banner renders null until the mount effect sets
  // `visible`, so this is hydration-safe.
  const isPartnerOrigin = !!readPartnerBookingOrigin();
  // Don't float this over the search results — its fixed right-edge position
  // overlaps each vehicle card's trust badges / deposit and competes with the
  // booking CTA on the conversion-critical page. Also never show the Sukuto
  // "Get App" banner on partner white-label storefronts (/partner/<slug>/...),
  // which must stay free of global Sukuto app promotion. The shared funnel
  // (/cart, /booking) and the user account pages (/user/...) live outside
  // /partner/, so a partner-origin session (sukuto_partner_origin cookie) is
  // suppressed there too — on all viewports.
  const hideOnRoute =
    /\/search\/?$/.test(pathname || "") ||
    /^\/partner(?:\/|$)/.test(pathname || "") ||
    ((/(?:\/cart|\/booking)\/?$/.test(pathname || "") ||
      /^\/user(?:\/|$)/.test(pathname || "")) &&
      isPartnerOrigin);

  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY)) return;
    } catch {}

    const ua = navigator.userAgent || "";
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time platform sniff for the store URL on mount
    if (/iphone|ipad|ipod/i.test(ua)) setStoreUrl(APP_STORE_URL);

    setVisible(true);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "true");
    } catch {}
  };

  if (!visible || hideOnRoute) return null;

  return (
    <div
      // Stable id so an open modal can hide this (globals.css,
      // `html[data-scroll-locked]`). At z-index 99999 it otherwise draws over
      // every overlay on the site.
      id="smart-app-banner"
      style={{
        position: "fixed",
        top: "50%",
        right: -40,
        zIndex: 99999,
        transform: "translateY(-50%) rotate(-90deg)",
        transformOrigin: "center center",
      }}
    >
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get App"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          // primary-deep (#c13333) gives white-on-bg 5.5:1 (WCAG AA); plain
          // --color-primary (#ff5a5f) was only 3.05:1.
          backgroundColor: "var(--color-primary-deep)",
          color: "white",
          padding: "8px 16px",
          borderRadius: "8px 8px 0 0",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textDecoration: "none",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.15)",
          whiteSpace: "nowrap",
        }}
      >
        📲 Get App
      </a>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss app banner"
        style={{
          position: "absolute",
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          background: "#fff",
          borderRadius: "50%",
          border: "none",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "var(--color-text-muted)",
          padding: 0,
          transform: "rotate(90deg)",
        }}
      >
        <svg width="7" height="7" viewBox="0 0 14 14" fill="none">
          <path
            d="M1 1L13 13M13 1L1 13"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default SmartAppBanner;
